import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';

import {projectTitleInitialState} from '../../reducers/project-title';
import {getIsShowingProject} from '../../reducers/project-state';
import {showAlertWithTimeout, showStandardAlert} from '../../reducers/alerts';
import {setProjectUnchanged} from '../../reducers/project-changed';
import {
    ccApiUrl,
    getConnectedCenterUploadUrl,
    getEditingGameId,
    isConnectedCenterMode
} from '../../lib/nexus-connected-center';
import {
    ccParentUpload,
    shouldUseParentBridge
} from '../../lib/nexus-cc-parent-bridge';
import {nexusDebugLog} from '../../lib/nexus-debug-log';
import {getProjectByteLength, projectContentToUint8Array} from '../../lib/nexus-project-bytes';
import {MenuItem} from '../menu/menu.jsx';

import menuBarStyles from './menu-bar.css';
import styles from './connected-center-publish.css';

class ConnectedCenterPublish extends React.Component {
    constructor (props) {
        super(props);
        this.state = {publishing: false};
        bindAll(this, ['publishToCommunity']);
    }

    isUpdating () {
        return Boolean(getEditingGameId());
    }

    submitProject () {
        if (this.state.publishing || !this.props.canSaveProject) {
            nexusDebugLog('NexusBlocks', 'Publish skipped', {
                publishing: this.state.publishing,
                canSaveProject: this.props.canSaveProject
            }, 'error');
            return;
        }

        const editingId = getEditingGameId();

        nexusDebugLog('NexusBlocks', 'Publish started', {
            editingId,
            useParentBridge: shouldUseParentBridge(),
            title: this.props.projectTitle,
            filename: this.props.projectFilename
        });

        this.setState({publishing: true});
        this.props.onShowSavingAlert();

        this.props.saveProjectSb3().then(content => {
            nexusDebugLog('NexusBlocks', 'Project serialized to .sb3', {
                bytes: getProjectByteLength(content),
                type: content && content.constructor && content.constructor.name
            });
            const title = this.props.projectTitle || 'Untitled Game';
            const filename = this.props.projectFilename;
            const visibility = 'public';

            if (shouldUseParentBridge()) {
                return ccParentUpload({
                    project: content,
                    filename,
                    title,
                    gameId: editingId,
                    visibility
                });
            }

            return projectContentToUint8Array(content).then(bytes => {
            const form = new FormData();
            const blob = new Blob([bytes], {type: 'application/octet-stream'});
            form.append('project', blob, filename);
            form.append('title', title);
            if (editingId) {
                form.append('game_id', String(editingId));
            } else {
                form.append('visibility', visibility);
            }

            const endpoint = editingId ?
                ccApiUrl('update_community_game.php') :
                getConnectedCenterUploadUrl();

            return fetch(endpoint, {
                method: 'POST',
                body: form,
                credentials: 'include'
            }).then(response => response.json().then(data => {
                if (!response.ok || data.error) {
                    throw new Error(data.error || 'Upload failed');
                }
                return data;
            }));
            });
        }).then(data => {
                nexusDebugLog('NexusBlocks', 'Publish succeeded', {
                    gameId: data && data.game && data.game.id,
                    title: data && data.game && data.game.title
                });
                this.props.onProjectUnchanged();
                if (this.isUpdating()) {
                    this.props.onShowUpdateSuccessAlert();
                } else {
                    this.props.onShowPublishSuccessAlert();
                }
                try {
                    if (window.parent && window.parent !== window) {
                        window.parent.postMessage({
                            type: 'cc-game-published',
                            game: data.game
                        }, '*');
                    }
                } catch (e) {
                    // ignore cross-origin postMessage errors
                }
            })
            .catch(err => {
                nexusDebugLog('NexusBlocks', 'Publish failed', {
                    error: err && err.message ? err.message : String(err)
                }, 'error');
                this.props.onShowPublishErrorAlert(err && err.message);
            })
            .finally(() => {
                this.setState({publishing: false});
            });
    }

    publishToCommunity () {
        this.submitProject();
    }

    render () {
        if (!isConnectedCenterMode()) {
            return null;
        }

        const updating = this.isUpdating();

        if (this.props.variant === 'menuItem') {
            return (
                <MenuItem onClick={this.publishToCommunity}>
                    {updating ? (
                        <FormattedMessage
                            defaultMessage="Update Project"
                            description="File menu item to update an existing community game"
                            id="tw.menuBar.updateProject"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Publish to Community"
                            description="File menu item to upload the project to Connected Center community games"
                            id="tw.menuBar.publishToCommunity"
                        />
                    )}
                </MenuItem>
            );
        }

        return (
            <div
                className={classNames(
                    menuBarStyles.menuBarItem,
                    menuBarStyles.menuBarGlassBtn,
                    menuBarStyles.hoverable,
                    {
                        [styles.glassBtnDisabled]: this.state.publishing || !this.props.canSaveProject
                    }
                )}
                role="button"
                tabIndex={0}
                title={updating ? 'Update this game on Connected Center' : 'Upload this game to Connected Center'}
                onClick={this.publishToCommunity}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.publishToCommunity();
                    }
                }}
            >
                <span className={styles.glassBtnLabel}>
                    {this.state.publishing ? (
                        <FormattedMessage
                            defaultMessage="Saving…"
                            description="Menu bar button while uploading to Connected Center"
                            id="tw.menuBar.publishingToCommunity"
                        />
                    ) : updating ? (
                        <FormattedMessage
                            defaultMessage="Update Project"
                            description="Menu bar button to update an existing community game"
                            id="tw.menuBar.updateProjectBtn"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Publish to Community"
                            description="Menu bar button to upload the project to Connected Center community games"
                            id="tw.menuBar.publishToCommunityBtn"
                        />
                    )}
                </span>
            </div>
        );
    }
}

ConnectedCenterPublish.propTypes = {
    canSaveProject: PropTypes.bool,
    className: PropTypes.string,
    onProjectUnchanged: PropTypes.func,
    onShowPublishErrorAlert: PropTypes.func,
    onShowPublishSuccessAlert: PropTypes.func,
    onShowSavingAlert: PropTypes.func,
    onShowUpdateSuccessAlert: PropTypes.func,
    projectFilename: PropTypes.string,
    projectTitle: PropTypes.string,
    saveProjectSb3: PropTypes.func,
    variant: PropTypes.oneOf(['button', 'menuItem'])
};

ConnectedCenterPublish.defaultProps = {
    variant: 'button'
};

const getProjectFilename = (curTitle, defaultTitle) => {
    let filenameTitle = curTitle;
    if (!filenameTitle || filenameTitle.length === 0) {
        filenameTitle = defaultTitle;
    }
    return `${filenameTitle.substring(0, 100)}.sb3`;
};

const mapStateToProps = state => ({
    canSaveProject: getIsShowingProject(state.scratchGui.projectState.loadingState),
    projectFilename: getProjectFilename(state.scratchGui.projectTitle, projectTitleInitialState),
    projectTitle: state.scratchGui.projectTitle,
    saveProjectSb3: state.scratchGui.vm.saveProjectSb3.bind(state.scratchGui.vm, 'arraybuffer'),
});

const mapDispatchToProps = dispatch => ({
    onProjectUnchanged: () => dispatch(setProjectUnchanged()),
    onShowSavingAlert: () => showAlertWithTimeout(dispatch, 'saving'),
    onShowPublishSuccessAlert: () => showAlertWithTimeout(dispatch, 'twPublishCommunitySuccess'),
    onShowUpdateSuccessAlert: () => showAlertWithTimeout(dispatch, 'twUpdateCommunitySuccess'),
    onShowPublishErrorAlert: () => dispatch(showStandardAlert('savingError'))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ConnectedCenterPublish);
